import { lazy, Suspense, useEffect } from 'react';
import type { ElementType } from 'react';
import {
   Modal,
   ModalOverlay,
   ModalContent,
   Skeleton,
   type UseDisclosureProps,
   useDisclosure,
   useBreakpointValue,
} from '@chakra-ui/react';
import type {
   AlgoliaHitProps,
   TreeNavigationProps,
   WorkbenchDevice,
   SelectionProps,
   AlgoliaConfig,
} from './types';
import type { LocaleCode } from '@ifixit/i18n';
import { addRecentDevice, type RecentDevice, safeLocalStorage } from '@ifixit/utils';
import { trackAnalyticsCustomEvent } from '@ifixit/analytics';

const NUM_RECENT_DEVICES = 2;
const DevicePicker = lazy(() => import('./DevicePicker'));

export const DevicePickerModal = ({
   onSelect,
   algoliaConfig,
   oniFixit,
   langid,
   storeLocale,
   devicesInWorkbench,
   initialDevice,
   selectionProps,
   treeNavigationProps,
   showUncategorizedDevices,
   titleInstruction,
   rootDevice,
   ifixitOrigin,
   wikiTextComponent,
   analyticsKey,
   ignoreAncestors = [],
   fullScreen = false,
   ...disclosureProps
}: {
   onSelect: (_device: AlgoliaHitProps) => void;
   algoliaConfig: AlgoliaConfig;
   oniFixit: boolean;
   langid: string;
   storeLocale: LocaleCode;
   devicesInWorkbench?: WorkbenchDevice[];
   initialDevice?: string;
   selectionProps?: SelectionProps;
   treeNavigationProps?: TreeNavigationProps;
   showUncategorizedDevices?: boolean;
   titleInstruction?: string;
   rootDevice?: string;
   ifixitOrigin?: string;
   wikiTextComponent?: ElementType;
   ignoreAncestors?: string[];
   analyticsKey: string;
   fullScreen?: boolean;
} & UseDisclosureProps) => {
   const { isOpen, onClose } = useDisclosure(disclosureProps);
   const isCentered = useBreakpointValue({ base: false, sm: true });
   const recentDevices = getRecentDevices();
   const initialQuery =
      typeof window === 'undefined'
         ? ''
         : new URLSearchParams(window.location.search).get('query') || '';

   const onSubmit = (device: AlgoliaHitProps) => {
      if (device.wikiid) {
         addRecentDevice(device);
      }
      onSelect(device);
   };

   const trackedClose = () => {
      trackAnalyticsCustomEvent({
         eventCategory: analyticsKey,
         eventAction: analyticsKey + ' - Picker - Cancel',
         eventName: '',
      });
      onClose();
   };

   useEffect(() => {
      if (isOpen) {
         trackAnalyticsCustomEvent({
            eventCategory: analyticsKey,
            eventAction: analyticsKey + ' - Picker - Open',
            eventName: '',
         });
      }
   }, [isOpen]);

   return (
      <Modal
         isOpen={isOpen}
         onClose={trackedClose}
         scrollBehavior="inside"
         isCentered={isCentered}
         closeOnEsc
         closeOnOverlayClick
         autoFocus={false}
      >
         <ModalOverlay />
         <ModalContent
            p={0}
            my={0}
            height={fullScreen ? '100vh' : { base: '90dvh', sm: '85vh' }}
            maxHeight={fullScreen ? '100%' : '800px'}
            width="auto"
            maxWidth="100%"
            alignSelf={{ base: 'flex-end', sm: 'auto' }}
            borderRadius={fullScreen ? '0px' : '8px'}
         >
            <Suspense fallback={<Skeleton height="100%" width="100%" />}>
               <DevicePicker
                  algoliaConfig={algoliaConfig}
                  onSubmit={onSubmit}
                  onCancel={onClose}
                  oniFixit={oniFixit}
                  langid={langid}
                  devicesInWorkbench={devicesInWorkbench}
                  initialDevice={initialDevice}
                  selectionProps={selectionProps}
                  treeNavigationProps={treeNavigationProps}
                  showUncategorizedDevices={showUncategorizedDevices}
                  titleInstruction={titleInstruction}
                  storeLocale={storeLocale}
                  recentDevices={recentDevices}
                  rootDevice={rootDevice}
                  ifixitOrigin={ifixitOrigin}
                  wikiTextComponent={wikiTextComponent}
                  analyticsKey={analyticsKey}
                  ignoreAncestors={ignoreAncestors}
                  initialQuery={initialQuery}
                  fullScreen={fullScreen}
               />
            </Suspense>
         </ModalContent>
      </Modal>
   );
};

export function getRecentDevices(limit: number = NUM_RECENT_DEVICES): RecentDevice[] {
   return safeLocalStorage.getJson<RecentDevice[]>('recentDevices')?.slice(0, limit) ?? [];
}
